import { useState, useEffect, useCallback } from 'react';
import { getAdminHealth, sendTestEmail, sendTestEmails, runFunnelTestApi, cleanupFunnelTestDataApi, getTestUsersPreview, deleteTestUsers } from '../../utils/api';
import { C, FONT, SectionHeader } from './adminShared';
import { useAuth } from '../../hooks/useAuth';

const SVC_LABELS = {
  backend:  { label: 'Backend Server',    icon: '⬡' },
  supabase: { label: 'Supabase (DB)',     icon: '◈' },
  stripe:   { label: 'Stripe (Payments)', icon: '◉' },
  resend:   { label: 'Resend (Email)',    icon: '✉' },
  env:      { label: 'Environment Vars',  icon: '▦' },
};

const svcStyle = (status) => {
  if (status === 'ok')   return { dot: C.green, text: C.green,   bg: C.greenBg, border: '#A8D5C0', label: 'Healthy' };
  if (status === 'warn') return { dot: C.amber, text: '#92650A', bg: C.amberBg, border: '#F5D69A', label: 'Warning' };
  return                        { dot: C.red,   text: C.red,     bg: C.redBg,   border: '#F5C6C0', label: 'Error'   };
};

const ServiceCard = ({ serviceKey, data }) => {
  const meta = SVC_LABELS[serviceKey] || { label: serviceKey, icon: '◦' };
  const s = svcStyle(data.status);
  return (
    <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 12, padding: '18px 20px', flex: 1, minWidth: 180 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16, opacity: 0.7 }}>{meta.icon}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{meta.label}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.dot }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: s.text }}>{s.label}</span>
        </div>
      </div>
      {data.latencyMs > 0 && (
        <div style={{ fontSize: 11, color: C.accent, marginBottom: 4 }}>
          Latency: <strong style={{ color: data.latencyMs > 1000 ? C.amber : C.text }}>{data.latencyMs}ms</strong>
        </div>
      )}
      {data.detail && <div style={{ fontSize: 11, color: s.text, opacity: 0.85 }}>{data.detail}</div>}
    </div>
  );
};

const fmtTime = (d) =>
  d ? new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';

const EMAIL_LIST = [
  { key: 'seq1',  label: 'Seq 1 — Masterclass Access',
    subject: 'Here\'s your access to "When Awareness Isn\'t Enough" ❤️',
    trigger: 'Fires immediately on opt-in.',
    preview: `Hey [First Name],

You're in — here's your access to the free masterclass:

[ BUTTON: Watch: When Awareness Isn't Enough → ]

In this masterclass you'll discover:
  • Why self-awareness alone isn't enough to break your patterns — and what actually is.
  • The hidden reasons you keep ending up in the same loops even when you know better.
  • What needs to shift at the root for real, lasting change to happen.
  • The first step toward feeling more Clarity, Calm, Confidence and Connection in your daily life.

This is a quick, 10 minute video...and honestly - it's one of those 'OMG penny drops, things click into place, ahhh - now I get it' kinda of videos!

So if you're self-aware, yet still trapped in old conditioning and simply tired of not being able to break free - take just 10 minutes of your time to watch this.

(You can thank me after)

[ BUTTON: Watch: When Awareness Isn't Enough → ]

Much love, and see you there!
Ben

P.S. If this lands for you, share it with someone who's been doing the work but still feels stuck. They'll thank you too.` },

  { key: 'seq1b', label: 'Seq 1B — Masterclass Reminder',
    subject: 'Did you get a chance to watch yet? 👀',
    trigger: '24h after opt-in — only if masterclass not yet watched.',
    preview: `Hey [First Name],

I know life can get distracting, so I wanted to remind you that your free Masterclass 'When Awareness Isn't Enough' is still waiting for you.

[ BUTTON: Watch: When Awareness Isn't Enough → ]

In this masterclass you'll discover:
  • Why your nervous system keeps pulling you back into old patterns even when you're aware of them.
  • How childhood conditioning is still shaping how you think, feel, and respond today.
  • The real reason you keep outsourcing your worth to other people without realising it.
  • Why change hasn't "stuck" so far — and what's been missing underneath it.
  • The real shift that allows you to finally start rebuilding your sense of self from the inside out.

This is a quick, 10 minute video...and honestly - it's one of those 'OMG penny drops, things click into place, ahhh - now I get it' kinda of videos!

So if you're self-aware, yet still trapped in old conditioning and simply tired of not being able to break free - take just 10 minutes of your time to watch this.

(You can thank me after)

[ BUTTON: Watch: When Awareness Isn't Enough → ]

Much love, and see you there!
Ben` },

  { key: 'seq2a', label: 'Seq 2 — Nurture Email 1',
    subject: 'If you\'re trapped in old conditioning....I bet you didn\'t know this!',
    trigger: 'After masterclass watched — Day 1 of nurture.',
    preview: `Hey [First Name],

Something I hear a lot from people who've been on their self-development journey for a while is this:

  "I get that I've been conditioned.
  I can see how my thoughts, emotions and behaviours have been shaped by my upbringing.
  And yet I'm still repeating the same old habits.
  And I'm simply exhausted from it."

I want to name something important here:

Just because you are now more aware of how much your upbringing has conditioned who you think you are, and what you think you're expected to do... doesn't mean that this is going to recondition you to think, feel and behave like your true authentic self.

It's an incredible start, don't get me wrong...it's just not quite enough.

Here are the gaps that need to be bridged if you're serious about breaking free of the conditioning you've 'picked up along the way', and if you're serious about reconnecting and realigning to the person you've started to see inside.

THE AWARENESS GAP.
Even when you're aware of your conditioning, there's still going to be things you don't know you don't know. Awareness of the pattern isn't the same as awareness of everything driving it and reinforcing it.

THE STRATEGY GAP.
Insight isn't a strategy. Recognising what's happening doesn't automatically teach your system something new. Awareness of your patterns is just that — it's awareness. And while it is necessary, it's simply not a process. And without the right process, awareness can often just get frustrating.

THE IDENTITY GAP.
Even when you want to change, part of you doesn't yet feel safe being different. Because those old patterns aren't just habits — they've become so familiar that they feel like you. And until that shifts at a level of identity, change stays just out of reach.

In my short 7 day course, Recondition Yourself — we will work together to bridge all three of these gaps, plus you'll get the tools to rebuild your 3 pillars and strengthen your relationship to yourself.

You deserve to break free of your conditioned patterns and live a life that aligns with who you really are.

[ BUTTON: Join Recondition Yourself → ]

Much love,
Ben` },

  { key: 'seq2b', label: 'Seq 2 — Nurture Email 2',
    subject: 'What\'s your relationship to yourself like?',
    trigger: 'Day 3 of nurture.',
    preview: `Hey [First Name],

I want to come back to something from the masterclass, because I think it's worth sitting with for a moment.

Your 3 pillars — Inner Ownership, Emotional Legitimacy, and Aligned Action — aren't just concepts to understand intellectually. They're the actual foundations that govern how you perceive your own self-worth. It's through your conditioning they've been eroded... and until they are strengthened and rebuilt, you will continue to repeat the same old patterns you are simply exhausted of repeating.

When Inner Ownership is strong, you stop questioning yourself so much. You trust your own thoughts and opinions more. And you stop feeling like you need everyone else to agree before you can move forward.

When Emotional Legitimacy is strong, your feelings stop feeling like such a big problem. You can feel something without it turning into a 'whole thing' that has you spiraling for days. And possibly most importantly, you stop feeling like there is something wrong with you for just having emotions.

And when Aligned Action is strong, your external life finally starts to reflect who you are internally. What a concept right? Living a life based on who you are inside — rather than a life you're expected to live.

Because here's the thing: you can be aware of every pattern you have, where it came from and why it's there... but you won't experience lasting change if your perceived self-value is still low.

And this is what's at the core of Recondition Yourself.
7 days.
Guided step by step trainings.
Grounded and practical.

There's a part of you that knows you deserve this — I think it's time you started listening more to that part.

[ BUTTON: Join Recondition Yourself → ]

Much love,
Ben` },

  { key: 'seq2c', label: 'Seq 2 — Nurture Email 3',
    subject: 'What\'s the real risk here?',
    trigger: 'Day 5 of nurture.',
    preview: `Hey [First Name],

I want to be honest with you about something.

I know that investing money and time — even a small amount — in something new can bring up hesitation.

  "What if it doesn't work?
  What if I'm not ready?
  What if it's just a waste of time and money?"

And honestly, those are all completely fair questions. But I think there is a bigger risk that possibly hasn't been considered... and I think it's important for you to sit with this before making up your mind.

Here's what I think the biggest risk is when considering Recondition Yourself. It's not the $69. And it isn't the 7 days.

THE REAL RISK IS STAYING WHERE YOU ARE.

Because if you've already done some self-development. If you've already read some books, listened to some podcasts or even possibly done some therapy... and you're still feeling trapped repeating old conditioned patterns... then it sounds like it's time to try a new approach.

So the real question isn't really: "Is this worth it?"

It's: "How much longer can I continue to shrink myself and live a life unaligned with who I really am?"

MY PROMISE TO YOU:
If you show up and complete all 7 days of training, attend your Breakthrough Mapping Session — and you genuinely feel it wasn't worth the money — let me know and I'll give you all your money back. An investment means you get more than what you put in. So either you get more than you put in — or you get your money back.

I'm so super passionate about this, because I was exactly in the same position you once were. Aware of my patterns. Aware of my self-talk. Aware I was not living up to my potential. And aware of how frustrating it was feeling stuck — feeling like nothing I tried seemed to change anything.

I know that feeling. I've been there. And I found a way out. And now I want that for you too!

See you inside — You can thank me later ;)

[ BUTTON: Join Recondition Yourself → ]

Much love,
Ben` },

  { key: 'seq3',  label: 'Seq 3 — Welcome + Credentials',
    subject: 'Welcome to Recondition Yourself — Your Portal is Ready',
    trigger: 'Fires immediately on purchase.',
    preview: `Welcome [First Name].

Congratulations on taking an incredibly important step on your journey to Reconditioning Yourself.
Your portal is ready — 7 days of powerful, transformative content built to help you reconnect and realign with who you truly are.

Here are your login credentials:
  Email:    [their email]
  Password: [auto-generated]
  Portal:   ry-portal.benmilston.com

We recommend changing your password after your first login — you can do this in your account settings.

[ BUTTON: Enter Your Portal → ]

Looking forward to growing with you, and if at any time you wish to reach out, either email me here or reach out via our FB community group.

Know that you have support going through this.

With belief in you,
Ben Milston
Transform Your Mind;
Transcend Your Life` },

  { key: 'seq4',  label: 'Seq 4 — Login Reminder',
    subject: 'Don\'t forget — You already paid for it! 👀',
    trigger: '48h after purchase — only if never logged in.',
    preview: `Hey [First Name],

Huge congratulations on saying YES to yourself and stepping into Recondition Yourself!

This program is about learning WHY you're still trapped in your conditioning — even though you're aware enough to know you're conditioned…

WHAT needs to shift at the core to get unstuck and start making some real internal and external changes in your life — that STICK…

And a step-by-step HOW to make these shifts…

So by the end of these 7 days, you feel less anxiety, overwhelm, overthinking, disconnect and self-doubt…

And more Clarity, Calm, Confidence and Connection with who you actually are underneath all the noise, self-judgement, fears and conditioning.

Buuuut… for any of that to actually start happening in real life, rather than just reading about how it can happen — there's an important secret I must let you in on…

  (You have to actually log in and start the program — purchasing it isn't quite enough for change to take place).

Here are your login details:
  Email:  [their email]
  Portal: ry-portal.benmilston.com

[ BUTTON: Click here to Log In and start your program → ]

Real clarity, direction and support are literally sitting there waiting for you. The door is open. But I can't walk through it for you. That part is yours.

See you inside!
Ben` },

  { key: 'seq5',  label: 'Seq 5 — Re-engagement',
    subject: 'Hey [name] — your course is waiting for you 👋',
    trigger: 'A few days inactive mid-course.',
    preview: `Hey [First Name],

I noticed you haven't logged into Recondition Yourself in a couple of days — and I just wanted to check in.

Life gets busy. That's real.

But I also know that the moments we most need to do this work are often the exact moments we find it hardest to make time for it.

Your progress, your journal entries, everything you've already built — it's all still there, exactly where you left it.

[ BUTTON: Continue Your Journey → ]

Your login details if you need them:
  Email: [their email]
  (Forgotten password? Use the reset option on the login page.)

You've already shown up for yourself once by joining this program. That wasn't a small thing.

Come back when you're ready — but don't let too much time pass. The version of you that started this week deserves to see it through.

With you all the way,
Ben` },

  { key: 'seq6',  label: 'Seq 6 — BMS Booking Nudge',
    subject: 'Quick reminder – book your Breakthrough Mapping Session ❤️',
    trigger: 'Day 1 complete — BMS not yet booked.',
    preview: `Hey [First Name],

I saw you've completed your onboarding for Recondition Yourself (awesome first step 👏) and hope you've enjoyed your first day.

As part of this program, you get a personalised 1:1 Breakthrough Mapping Session — it's already been paid for — you just haven't booked it in yet.

This session is a key part of your journey, and a chance where we can really apply what you've learned in this course to YOU. It's an opportunity to take the investment that you've already made and really get the most out of this week, so it's not just another week gone by.

It's where we:
  • Go deeper into your loops and outline the multi-layer aspects surrounding your loops.
  • Clarify any questions you have and help you break through any blocks that have shown up over the week.
  • Make sure you know exactly what to focus on and how — so that you have a clear direction forward once these 7 days are over.

It's here to help you get the maximum value from this course, and to ensure you have the right kind of personalised support you need, rather than just delivering an 'it's all on you good luck' type of course.

[ BUTTON: Click here to book your Breakthrough Mapping Session → ]

Note — you will only be able to book this session from 7 days of making the booking. This is to ensure you have first completed the course and are able to come prepared with your loops, blocks and questions.

Really glad you're here with us!
Much love,
Ben` },

  { key: 'seq7a', label: 'Seq 7A — Booking Confirmation',
    subject: 'Your 1:1 Breakthrough Mapping Session is officially booked ❤️',
    trigger: 'Fires immediately when Booking Hub records a BMS appointment (via /webhooks/booking-hub/appointment). Booking Hub skips its own client confirmation for RY Portal users.',
    preview: `Hey [First Name],

Your 1:1 Breakthrough Mapping Session is officially booked — and I'm super excited for you.

This session is an important part of Recondition Yourself. Because while these 7 days will give you an incredible foundation, with powerful tools, language and awareness, there are naturally still things that are simply much harder to see when you're the one living inside the patterns.

That's where this session comes in. It helps bridge the gap between what you're able to notice on your own… and what becomes possible when we look at it together.

This session helps bridge:
  ✨ The Awareness Gap — helping you see parts of your loops that are difficult to spot from inside them.
  ✨ The Time Gap — so you don't have to keep repeating the same patterns for months or years just to slowly understand what's really going on.
  ✨ The Experience Gap — passing on knowledge from my own experiences and mistakes — so you don't have to go through the same experiences yourself to gain the same knowledge.

And just as importantly, this is a space where you get to speak to someone who genuinely understands what you're going through. A space to feel seen. Supported. Understood. And guided.

Here's what you'll walk away with:
  ✨ Deeper clarity on the loop(s) you'll be working with.
  ✨ A clearer understanding of what's keeping the pattern in place.
  ✨ Direction for what to keep working on after the 7 days.

[ SESSION DETAILS BOX: date / time / Zoom link ]

Action step: Make sure this session is in your calendar and that your reminder is set. Come as you are — open, honest, curious and willing to explore what's really been going on beneath the surface.

Much love and gratitude,
Ben` },

  { key: 'seq7b', label: 'Seq 7B — Pre-call Reminder (3 days)',
    subject: 'A little note before our Breakthrough Mapping Session',
    trigger: '3 days before session_date (from ry_bookings). Booking Hub sends no reminder of its own for RY users.',
    preview: `Hey [First Name],

Your 1:1 Breakthrough Mapping Session on [date] at [time] is coming up, and I want to help you get the most out of our time together.

I want you to know this session is here for a reason. There's only so much a 7-day course can do on its own. Not because the course isn't powerful — but because some things are simply much easier to see, understand and work through with personalised support — from someone who's been there.

When you're the one inside the loop, it can be really hard to spot:
  • What else you're missing.
  • The small subtle words you use that hold a lot of power in how you feel and relate to yourself.
  • What else needs challenging.
  • Where you're still abandoning yourself without fully realising it.

This course is about bridging the gaps between who you've thought you needed to be to feel loved, accepted, connected and worthy… About who you learned to be… And who you truly are inside. And this 1:1 session will help you get there faster, deeper, and with far less guesswork.

Action step — before we meet, take a few moments to reflect on:
  • What loop or pattern has felt most on the surface for you lately?
  • What feels hardest to understand or most confusing for you?
  • Where do you feel most stuck and why do you believe you're getting stuck there?
  • What would make this session feel genuinely supportive and useful for you?

Please double-check your calendar so you can be fully present. No multitasking. No rushing. No squeezing it in between things. This time is for you.

Zoom link: [link]

See you soon,
Ben

P.S. If possible, make sure you've moved through the full 7 days of Recondition Yourself before our session. That way, we can spend less time covering foundations and more time going deeper into what's specific to you.` },

  { key: 'seq7c', label: 'Seq 7C — Final Reminder (1 day)',
    subject: 'Your Breakthrough Mapping Session is tomorrow ❤️',
    trigger: '1 day before session_date (from ry_bookings). Booking Hub sends no reminder of its own for RY users.',
    preview: `Hey [First Name],

Your 1:1 Breakthrough Mapping Session is tomorrow — [date] at [time] — and I want to help you show up as prepared as possible.

I want you to know this session is here for a reason. There's only so much a 7-day course can do on its own. Not because the course isn't powerful — but because some things are simply much easier to see, understand and work through with personalised support — from someone who's been there.

When you're the one inside the loop, it can be really hard to spot:
  • What else you're missing.
  • The small subtle words you use that hold a lot of power in how you feel and relate to yourself.
  • What else needs challenging.
  • Where you're still abandoning yourself without fully realising it.

Before tomorrow, take a moment to reflect on:
  • What loop or pattern has felt most on the surface for you lately?
  • What feels hardest to understand or most confusing for you?
  • Where do you feel most stuck and why do you believe you're getting stuck there?
  • What would make this session feel genuinely supportive and useful for you?

Please double-check your calendar so you can be fully present. No multitasking. No rushing. No squeezing it in between things. This time is for you.

[ SESSION DETAILS BOX: date / time / Zoom link ]

See you tomorrow!
Much love,
Ben` },

  { key: 'seq8a', label: 'Seq 8 — UUU Email 1',
    subject: 'Hey [firstName], here\'s something worth considering',
    trigger: '48hrs after BMS session_date — ONLY if no UUU purchase made (booking_client_offers check).',
    preview: `Hey [First Name],

I'm hoping by now after these last 7 days inside Recondition Yourself, you're not just feeling like you've just "completed another course."

You've been looking directly at your conditioning and loops — the ways you've been thinking, feeling and reacting on autopilot for years — and have started to understand them in a way that actually makes sense for you.

For many people, this week will have brought them:

MORE CLARITY — seeing why you keep getting pulled into the same loops, what's actually running them under the surface, and what would genuinely need to shift for things to feel different.

MORE CALM — little pockets of steadiness in your body and mind, less spiralling, softer self-talk, and more compassion for yourself, even in moments that used to send you into anxiety or pressure.

MORE CONFIDENCE — not necessarily loud, external confidence yet, but a quiet inner knowing: "I do deserve to work on this. I'm not broken. I am actually allowed to be more me."

MORE CONNECTION — more moments of feeling like you're actually with yourself, kinder towards yourself and a bit more present in your life instead of performing to meet other people's expectations.

You've started to strengthen your Three Pillars — owning your perspective more, allowing your emotions without automatically feeling like they're 'wrong', and taking small, aligned steps that reflect you instead of just keeping everyone else comfortable.

And honestly — all of this is huge and I'm so celebrating you for the work you've already put in.
You're doing the work most people avoid their entire lives.

And the thing is, you can keep going on your own. You really can.

But here's the part most people don't realise about this work — which you may have experienced in our Breakthrough Mapping Session…

  "It's really hard to uncover, internalise and shift these patterns on your own, when you've spent your whole life living inside of them."

What you've been able to touch in Recondition Yourself is the foundation:
  • You've named some of the louder loops.
  • You've seen where you've given more worth to outside perspectives, opinions and values, and where your Pillars have eroded because of it.

And while those first shifts matter so much… there is still more running under the surface that can quietly pull you back into those familiar patterns.

This is where personalised 1:1 guidance can really make the difference.

FOR JUST $333.33/MONTH FOR 6 MONTHS (saving $800 off full price), you get 16 weeks of 1:1 guidance to:
  • Map and work through your deeper loops.
  • Continue strengthening your 3 Pillars so the 4 C's become more consistent.
  • Support you in ways back to your true self that the people around you simply can't.

  • Your mornings can feel even calmer as you wake up into a life YOU are choosing.
  • Your relationships can feel lighter and more honest.
  • Your actions can become more grounded and self-led.
  • Your whole experience of life can brighten.

[ BUTTON: Book Your Unlock. Unlimit. Unleash 1:1 Journey → ]

If inside you feel this is right for you, but you're met with fear, doubt, or discomfort about the investment — I get it. But the question is: will you let these emotions continue to control your actions, or will you allow yourself to feel this and still take the aligned action you know you want.

[ BUTTON: Unlock. Unlimit. Unleash – In 16 Weeks → ]

With full belief in you,
Ben` },

  { key: 'seq8b', label: 'Seq 8 — UUU Email 2', subject: 'What would your future self thank you for?',
    trigger: '1 day after email 1 · 6 days remaining on offer.',
    preview: `Hey [First Name],

I want to let you in on something…

There is a catch to this 1:1 offer — but it's not the cost.

The real catch is this: we don't live to regret the things we do in life. We regret the things we don't do.

This $333.33/month offer (saving you $800) is only available for 6 more days… and once it closes, it's gone.

But the real question isn't "should I spend this money?" Because this isn't about the money.

It's: "What will my future self thank me for?"

[Feature box: 1×120min session, 15×60min sessions, personalised guidance, text+voice support, accountability, someone who truly understands this journey]

You can do this alone, but you don't have to anymore.

With you all the way, Much love,
Ben

P.S. If something feels unclear or you want to talk it through, just hit reply — I'm here.` },

  { key: 'seq8c', label: 'Seq 8 — UUU Email 3', subject: 'Let\'s try something together for a moment… (5 days left)',
    trigger: '2 days after email 1 · 5 days remaining on offer.',
    preview: `Hey [First Name],

Let's try something together for a moment.

I want you to think back to where you were just before you joined Recondition Yourself.

What did you think back then, that you know to be different now? What didn't you know back then that you now know?

Think about how much has been able to click in just 7 focused days — more clarity, calm, confidence, connection.

Now, let's fast-forward to two possible timelines 4 months down the road.

Timeline One: Life got in the way again. Bit by bit you've started to revert to old patterns.

Timeline Two: You recognised RY was a foundation and secured 1:1 support. After 120 days your clarity is deeper, your calm more grounded, your confidence lived, your connection richer.

For just $333.33 per month for 6 months (btw, $800 cheaper than if you hadn't come through Recondition Yourself) — but only for the next 5 days.

With you all the way — because I believe you're worth it,
Ben

P.S. If you want to talk anything through — fears, questions, doubts, whatever — just hit reply. I'm here for you.` },

  { key: 'seq8d', label: 'Seq 8 — UUU Email 4', subject: 'The wobble after a breakthrough (what isn\'t talked about)',
    trigger: '3 days after email 1 · 4 days remaining on offer.',
    preview: `Hey [First Name],

There's a specific moment in this work that hardly anyone talks about.

It's the moments straight after the breakthroughs.

Life gets loud again. Work. Family. Old expectations. And then — someone makes a comment. A familiar trigger shows up. And suddenly you're thinking: "Wait… why is this still so hard?"

This is the wobble after a breakthrough.

It doesn't mean you've gone backwards. It means you're at the exact point where most people quietly give up on themselves.

This is the point where your Pillars are either slowly strengthened through support, repetition and guidance — or slowly eroded again by the same old pressures and patterns.

You have 4 more days to take advantage of this special rate:
$333.33/month · 16 weeks of 1:1 support · $800 off full price

Those who get support at this exact point are the ones who turn "this course I once did" into "this was the moment everything really changed."

With you all the way,
Ben

P.S. If you are in the wobble right now — hit reply and tell me what's coming up. I'm here.` },

  { key: 'seq8e', label: 'Seq 8 — UUU Email 5', subject: 'Can I ask you something honest? (3 days left btw)',
    trigger: '4 days after email 1 · 3 days remaining on offer.',
    preview: `Hey [First Name],

Let's be real for a second — no fluff.

In just 7 days you've already created some internal shifts. And that's truly AMAZING!

But the end of the program isn't the end of your journey. It's only the beginning.

You've got 72 hours left to decide whether you're ready to step into this next chapter — at your special Recondition Yourself rate… or whether you'll be taking a backseat and watch your old patterns and conditioning convince you why now isn't the right time.

A lot of people wait. They tell themselves they'll "see how they're doing later." But later usually turns into months, or years.

[Feature box: Weekly sessions, personalised insight, text+voice support, accountability, faster clearer path]
$333.33/month for 6 months — $800 off the normal investment

Your future self won't remember the discomfort of making this decision. They'll remember whether you backed yourself when you had the chance.

I don't want to see you continue to survive — I want to see you thrive!

With much love,
Ben

P.S. If something still feels unclear or you want to talk it through, hit reply — I'm here.` },

  { key: 'seq8f', label: 'Seq 8 — UUU Email 6', subject: '48hrs Left - Just imagine the story you could be telling 4 months from now',
    trigger: '5 days after email 1 · 48 hours remaining on offer.',
    preview: `Hey [First Name],

How do you feel about a quick visualisation exercise?

Take a second and imagine yourself 4 months from now…

You wake up feeling calmer, happier and more energised. Your mind is clearer. Your body less tense. And you actually notice a real smile on your face when you look in the mirror.

You feel more like yourself again — not the version you were trained to be, or the version who others expected you to be — but the one you've been getting glimpses of this past week.

You have 48 hours left to grab a spot at the lowest price you'll ever see the program at — more than $800 OFF its regular price.

You're closer than you think. One decision to shape the next 4 months… and the next 4 months could transform everything.

Btw, your future self thanks you ;)

Much love,
Ben` },

  { key: 'seq8g', label: 'Seq 8 — UUU Email 7', subject: 'Last call - No time like the now!',
    trigger: '6 days after email 1 · final 24 hours on offer.',
    preview: `Hey [First Name],

This is it. Final hours. Final reminder.

At midnight tonight, the $333.33/month (6 month plan) offer for 16 weeks of Unlock. Unlimit. Unleash personalised 1:1 guidance closes — and the price returns to the full amount. No exceptions. No extensions.

[Feature box: Weekly sessions, text+voice support, personalised plan, accountability]

The discomfort of making this decision lasts minutes — if not seconds. Which is then quickly followed by a feeling of pride in yourself. Followed by excitement. Followed by a lifetime of real internal and external change.

You've already done the courageous first step by joining Recondition Yourself. You've already proved you're worth the investment.

Most people in the world don't invest in themselves, because most people don't even realise their perceived self-value has been eroded… You have an opportunity to be different. To be more YOU!

With love and a belief in YOU!
Ben

P.S. This offer closes at midnight. After that, the price returns to full. This is your last chance.` },
];

export const AdminHealth = () => {
  const { user } = useAuth();
  const [data, setData]                   = useState(null);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);
  const [lastRun, setLastRun]             = useState(null);
  const [emailPanelOpen, setEmailPanelOpen] = useState(false);
  const [expandedEmail, setExpandedEmail] = useState(null);
  const [sending, setSending]             = useState({});
  const [sentStatus, setSentStatus]       = useState({});
  const [sendingAll, setSendingAll]       = useState(false);
  const [sendAllResult, setSendAllResult] = useState(null); // { ok, sent, total } | { ok:false }

  // Funnel test state
  const [testRunning, setTestRunning]       = useState(false);
  const [testResult, setTestResult]         = useState(null);
  const [testError, setTestError]           = useState(null);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupMessage, setCleanupMessage] = useState(null);

  // ── Test Data Cleanup state ──────────────────────────────────────────────
  // Flow: idle → previewing → preview-loaded → (confirm-modal) → deleting → success | error
  const [tdcStage, setTdcStage]                   = useState('idle'); // idle|previewing|preview|confirm|deleting|success
  const [tdcPreview, setTdcPreview]               = useState(null);   // { count, users[] }
  const [tdcResult, setTdcResult]                 = useState(null);   // { deletedSummary, deletedUsers }
  const [tdcError, setTdcError]                   = useState(null);
  const [tdcConfirmInput, setTdcConfirmInput]     = useState('');
  const [selectedUserIds, setSelectedUserIds]     = useState(new Set()); // which users are checked

  async function tdcFindUsers() {
    setTdcStage('previewing');
    setTdcError(null);
    try {
      const res = await getTestUsersPreview();
      setTdcPreview(res.data);
      // Default: all users ticked
      setSelectedUserIds(new Set((res.data.users || []).map(u => u.id)));
      setTdcStage('preview');
    } catch (err) {
      setTdcError(err.response?.data?.error || err.message);
      setTdcStage('idle');
    }
  }

  function tdcToggleUser(id, checked) {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function tdcOpenConfirm() {
    setTdcConfirmInput('');
    setTdcError(null);
    setTdcStage('confirm');
  }

  async function tdcConfirmDelete() {
    if (tdcConfirmInput !== 'DELETE TEST USERS') return;
    setTdcStage('deleting');
    setTdcError(null);
    try {
      const res = await deleteTestUsers(Array.from(selectedUserIds));
      setTdcResult(res.data);
      setTdcStage('success');
    } catch (err) {
      setTdcError(err.response?.data?.error || err.message);
      setTdcStage('confirm'); // keep modal open so admin can retry
    }
  }

  function tdcReset() {
    setTdcStage('idle');
    setTdcPreview(null);
    setTdcResult(null);
    setTdcError(null);
    setTdcConfirmInput('');
    setSelectedUserIds(new Set());
  }

  async function runFunnelTest() {
    setTestRunning(true);
    setTestError(null);
    setTestResult(null);
    try {
      const res = await runFunnelTestApi();
      setTestResult(res.data);
    } catch (err) {
      setTestError(err.response?.data?.error || err.message || 'Test failed');
    } finally {
      setTestRunning(false);
    }
  }

  function downloadReport() {
    if (!testResult?.reportMarkdown) return;
    const blob = new Blob([testResult.reportMarkdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    a.href = url;
    a.download = `funnel-test-report-${stamp}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function cleanupTestData() {
    setCleanupRunning(true);
    setCleanupMessage(null);
    try {
      const res = await cleanupFunnelTestDataApi();
      setCleanupMessage(`Deleted ${res.data.deleted} test row(s).`);
    } catch (err) {
      setCleanupMessage(`Cleanup failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setCleanupRunning(false);
    }
  }

  const runCheck = useCallback(() => {
    setLoading(true);
    setError(null);
    getAdminHealth()
      .then(r => { setData(r.data); setLastRun(new Date()); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => { runCheck(); }, [runCheck]);

  const handleSendOne = (key) => {
    setSending(s => ({ ...s, [key]: true }));
    setSentStatus(s => ({ ...s, [key]: null }));
    sendTestEmail(key)
      .then(() => { setSentStatus(s => ({ ...s, [key]: 'sent' })); })
      .catch(() => { setSentStatus(s => ({ ...s, [key]: 'error' })); })
      .finally(() => { setSending(s => ({ ...s, [key]: false })); });
  };

  // Bulk send: fires the sibling POST /api/admin/send-test-emails endpoint,
  // which sends every template to ADMIN_EMAIL. Response shape: { sent, errors, results }.
  const handleSendAll = () => {
    if (sendingAll) return; // re-entrancy guard
    setSendingAll(true);
    setSendAllResult(null);
    sendTestEmails()
      .then((res) => {
        const d = res?.data || {};
        const sent  = typeof d.sent === 'number' ? d.sent : null;
        const total = (typeof d.sent === 'number' && typeof d.errors === 'number') ? d.sent + d.errors : null;
        setSendAllResult({ ok: true, sent, total });
      })
      .catch(() => { setSendAllResult({ ok: false }); })
      .finally(() => { setSendingAll(false); });
  };

  const services = data?.services || {};
  const overall  = data?.overall;
  const overallStyle = overall === 'ok'
    ? { color: C.green, bg: C.greenBg, border: '#A8D5C0', label: 'All Systems Operational' }
    : { color: C.red,   bg: C.redBg,   border: '#F5C6C0', label: 'Issues Detected' };

  return (
    <div style={{ fontFamily: FONT, color: C.text }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.heading }}>System Health</div>
          <div style={{ fontSize: 11, color: C.accent, marginTop: 2 }}>Last checked: {fmtTime(lastRun)}</div>
        </div>
        <button onClick={runCheck} disabled={loading} style={{ padding: '9px 20px', background: loading ? C.border : C.heading, color: loading ? C.accent : '#FFF8F7', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: loading ? 'default' : 'pointer', fontFamily: FONT, transition: 'background 0.15s' }}>
          {loading ? 'Checking…' : '↻ Run Health Check'}
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div style={{ background: C.redBg, border: '1px solid #F5C6C0', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: C.red }}>
          ✗ Health check failed: {error}
        </div>
      )}

      {/* Overall status banner */}
      {data && (
        <div style={{ background: overallStyle.bg, border: `1px solid ${overallStyle.border}`, borderRadius: 12, padding: '14px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 12, height: 12, borderRadius: '50%', background: overallStyle.color, flexShrink: 0 }} />
          <span style={{ fontSize: 14, fontWeight: 700, color: overallStyle.color }}>{overallStyle.label}</span>
          <span style={{ fontSize: 11, color: C.accent, marginLeft: 'auto' }}>Checked at {fmtTime(lastRun)}</span>
        </div>
      )}

      {/* Service cards */}
      {data && (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 28 }}>
          {Object.entries(services).map(([key, svc]) => <ServiceCard key={key} serviceKey={key} data={svc} />)}
        </div>
      )}

      {/* Send Test Emails — expandable panel (owner-only) */}
      {user?.role === 'owner' && (
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, marginBottom: 20, overflow: 'hidden' }}>
        <div onClick={() => setEmailPanelOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', cursor: 'pointer', userSelect: 'none' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.heading }}>Send Test Emails</div>
            <div style={{ fontSize: 11, color: C.accent, marginTop: 2 }}>Click to expand — send any email individually to your inbox</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {sendAllResult?.ok && (
              <span style={{ fontSize: 11, fontWeight: 700, color: C.green, whiteSpace: 'nowrap' }}>
                {sendAllResult.total != null ? `✓ Sent ${sendAllResult.sent} of ${sendAllResult.total}` : '✓ All test emails sent'}
              </span>
            )}
            {sendAllResult && !sendAllResult.ok && (
              <span style={{ fontSize: 11, fontWeight: 700, color: C.red, whiteSpace: 'nowrap' }}>✗ Send failed — try again</span>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); handleSendAll(); }}
              disabled={sendingAll}
              style={{ padding: '7px 16px', background: sendingAll ? C.border : C.amber, color: sendingAll ? C.accent : C.text, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: sendingAll ? 'default' : 'pointer', fontFamily: FONT, whiteSpace: 'nowrap' }}
            >
              {sendingAll ? 'Sending…' : '✉ Send all test emails'}
            </button>
            <span style={{ fontSize: 11, color: C.accent }}>{EMAIL_LIST.length} emails</span>
            <span style={{ fontSize: 14, color: C.accent, transition: 'transform 0.2s', display: 'inline-block', transform: emailPanelOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
          </div>
        </div>

        {emailPanelOpen && (
          <div style={{ borderTop: `1px solid ${C.border}` }}>
            {EMAIL_LIST.map((email, i) => {
              const isSending     = sending[email.key];
              const status        = sentStatus[email.key];
              const isPlaceholder = email.subject === 'PLACEHOLDER';
              const isExpanded    = expandedEmail === email.key;
              return (
                <div key={email.key} style={{ borderBottom: i < EMAIL_LIST.length - 1 ? `1px solid ${C.border}` : 'none', background: isExpanded ? '#FFF8F0' : (i % 2 === 0 ? C.card : '#FDFAF8') }}>

                  {/* Row header */}
                  <div onClick={() => setExpandedEmail(isExpanded ? null : email.key)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 22px', cursor: 'pointer', userSelect: 'none', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{email.label}</div>
                      <div style={{ fontSize: 11, color: isPlaceholder ? C.amber : C.accent, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {isPlaceholder ? '⚠ Subject not yet written' : email.subject}
                      </div>
                    </div>
                    <span style={{ fontSize: 12, color: C.accent, transition: 'transform 0.15s', display: 'inline-block', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}>▼</span>
                  </div>

                  {/* Expanded detail panel */}
                  {isExpanded && (
                    <div style={{ padding: '0 22px 16px', borderTop: `1px solid ${C.border}` }}>
                      {email.trigger && (
                        <>
                          <div style={{ fontSize: 11, color: C.accent, fontWeight: 700, marginTop: 12, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Trigger</div>
                          <div style={{ fontSize: 12, color: C.text, marginBottom: 12 }}>{email.trigger}</div>
                        </>
                      )}
                      <div style={{ fontSize: 11, color: C.accent, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Copy</div>
                      <div style={{ fontSize: 12, color: C.text, lineHeight: 1.7, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 14, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                        {email.preview}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {status === 'sent'  && <span style={{ fontSize: 11, fontWeight: 700, color: C.green }}>✓ Sent to inbox</span>}
                        {status === 'error' && <span style={{ fontSize: 11, fontWeight: 700, color: C.red }}>✗ Send failed</span>}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleSendOne(email.key); }}
                          disabled={isSending || isPlaceholder}
                          style={{ padding: '7px 16px', background: isSending || isPlaceholder ? C.border : C.amber, color: isSending || isPlaceholder ? C.accent : C.text, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: isSending || isPlaceholder ? 'default' : 'pointer', fontFamily: FONT, whiteSpace: 'nowrap' }}
                        >
                          {isSending ? 'Sending…' : '✉ Send Test Email'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* Notes */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 22px' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.heading, marginBottom: 12 }}>Notes</div>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: C.accent, lineHeight: 2 }}>
          <li>Supabase ping queries the <code>ry_users</code> table (count only, no data returned)</li>
          <li>Stripe ping calls <code>balance.retrieve()</code> — confirms key validity</li>
          <li>Resend is checked by key presence only (no live API call to avoid unintended billing)</li>
          <li>Cron job runs daily at 08:00 UTC via cron-job.org — check externally if sequences aren't firing</li>
          <li>Backend latency of 0ms = no network hop required (local check)</li>
        </ul>
      </div>

      {/* Funnel Test (owner-only) */}
      {user?.role === 'owner' && (
      <div style={{
        background: C.card,
        borderRadius: 16,
        padding: '24px 28px',
        marginTop: 24,
        border: `1px solid ${C.border}`,
        boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
      }}>
        <h3 style={{
          fontFamily: "'Forum', serif",
          fontSize: '1.1rem',
          color: C.heading,
          marginBottom: 6,
          marginTop: 0,
          letterSpacing: '0.04em',
        }}>
          Funnel Test
        </h3>
        <p style={{
          fontSize: 13,
          color: C.accent,
          lineHeight: 1.6,
          marginBottom: 18,
          marginTop: 0,
        }}>
          Runs an end-to-end test of every tag in the funnel system, verifies the auto-removal and cron rules, and generates a downloadable report you can review or share.
        </p>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <button
            onClick={runFunnelTest}
            disabled={testRunning}
            style={{
              background: testRunning ? C.barTrack : C.amber,
              color: testRunning ? C.accent : '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '10px 20px',
              fontSize: 13,
              fontWeight: 600,
              cursor: testRunning ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              fontFamily: FONT,
            }}
          >
            {testRunning ? 'Running test…' : 'Run Funnel Test'}
          </button>

          <button
            onClick={cleanupTestData}
            disabled={cleanupRunning}
            style={{
              background: 'transparent',
              color: C.accent,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              padding: '10px 16px',
              fontSize: 12,
              fontWeight: 500,
              cursor: cleanupRunning ? 'not-allowed' : 'pointer',
              fontFamily: FONT,
            }}
          >
            {cleanupRunning ? 'Cleaning…' : 'Clean up test data'}
          </button>
        </div>

        {cleanupMessage && (
          <p style={{ fontSize: 12, color: C.accent, marginBottom: 12, marginTop: 0 }}>
            {cleanupMessage}
          </p>
        )}

        {testError && (
          <div style={{
            background: C.redBg,
            border: '1px solid #F5C6C0',
            borderRadius: 8,
            padding: '12px 16px',
            color: C.red,
            fontSize: 13,
          }}>
            ❌ {testError}
          </div>
        )}

        {testResult && (
          <div style={{
            background: C.amberBg,
            borderRadius: 10,
            padding: '16px 18px',
            border: `1px solid ${C.border}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontSize: 12, color: C.accent, marginBottom: 2 }}>Test contact:</div>
                <code style={{ fontSize: 12, color: C.heading, background: C.card, padding: '2px 6px', borderRadius: 4 }}>
                  {testResult.testEmail}
                </code>
              </div>
              <div style={{
                fontSize: 13,
                fontWeight: 600,
                padding: '6px 12px',
                borderRadius: 20,
                background: testResult.anomalyCount === 0 ? '#DCFCE7' : '#FEF3C7',
                color: testResult.anomalyCount === 0 ? '#166534' : '#92400E',
              }}>
                {testResult.anomalyCount === 0
                  ? '✅ All checks passed'
                  : `⚠️ ${testResult.anomalyCount} anomalies`}
              </div>
            </div>

            <button
              onClick={downloadReport}
              style={{
                background: C.heading,
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '8px 16px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                marginBottom: 14,
                fontFamily: FONT,
              }}
            >
              ⬇ Download report
            </button>

            <details style={{ fontSize: 12, color: C.accent }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600, marginBottom: 8 }}>
                Preview report
              </summary>
              <pre style={{
                background: C.card,
                padding: 12,
                borderRadius: 6,
                fontSize: 11,
                lineHeight: 1.5,
                overflowX: 'auto',
                maxHeight: 300,
                whiteSpace: 'pre-wrap',
                color: C.text,
                border: `1px solid ${C.border}`,
                marginTop: 8,
              }}>
                {testResult.reportMarkdown}
              </pre>
            </details>
          </div>
        )}
      </div>
      )}

      {/* ── Test Data Cleanup (owner-only) ──────────────────────────────── */}
      {user?.role === 'owner' && (
      <div style={{
        marginTop: 30,
        background: C.card,
        borderRadius: 14,
        padding: '24px 26px',
        border: `1px solid ${C.border}`,
      }}>
        <h3 style={{
          fontFamily: "'Forum', serif",
          fontSize: '1.1rem',
          color: C.heading,
          marginBottom: 6,
          marginTop: 0,
          letterSpacing: '0.04em',
        }}>
          Test Data Cleanup
        </h3>
        <p style={{
          fontSize: 13,
          color: C.accent,
          lineHeight: 1.6,
          marginBottom: 18,
          marginTop: 0,
        }}>
          Finds users whose first or last name contains 'test' (e.g. test, tester, testing). Untick anyone who's a real client before deleting.
        </p>

        {/* Idle state: just the button */}
        {tdcStage === 'idle' && (
          <button
            onClick={tdcFindUsers}
            style={{
              background: C.amber, color: '#fff', border: 'none',
              borderRadius: 8, padding: '10px 20px', fontSize: 13,
              fontWeight: 600, cursor: 'pointer', fontFamily: FONT,
            }}
          >
            Find test users
          </button>
        )}

        {tdcStage === 'previewing' && (
          <p style={{ fontSize: 13, color: C.accent, margin: 0 }}>Searching…</p>
        )}

        {tdcError && tdcStage !== 'confirm' && (
          <div style={{
            background: C.redBg, border: '1px solid #F5C6C0', borderRadius: 8,
            padding: '12px 16px', color: C.red, fontSize: 13, marginTop: 12,
          }}>
            ❌ {tdcError}
          </div>
        )}

        {/* Preview panel */}
        {tdcStage === 'preview' && tdcPreview && (
          <div style={{
            marginTop: 4,
            background: C.amberBg,
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            padding: '16px 18px',
          }}>
            {tdcPreview.count === 0 ? (
              <div>
                <p style={{ margin: '0 0 12px', fontSize: 13, color: C.text }}>
                  No test users found.
                </p>
                <button
                  onClick={tdcReset}
                  style={{
                    background: 'transparent', color: C.accent,
                    border: `1px solid ${C.border}`, borderRadius: 8,
                    padding: '8px 14px', fontSize: 12, fontWeight: 500,
                    cursor: 'pointer', fontFamily: FONT,
                  }}
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <p style={{ margin: '0 0 12px', fontSize: 13, color: C.text, fontWeight: 600 }}>
                  Found {tdcPreview.count} test user{tdcPreview.count === 1 ? '' : 's'}:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                  {tdcPreview.users.map(u => {
                    const r = u.relatedCounts;
                    const lines = [];
                    if (r.email_events)          lines.push(`${r.email_events} email${r.email_events === 1 ? '' : 's'}`);
                    if (r.contact_tags)          lines.push(`${r.contact_tags} tag${r.contact_tags === 1 ? '' : 's'}`);
                    const ryRows = r.ry_progress + r.ry_journal_entries + r.ry_checkins + r.ry_testimonials + r.ry_bms_forms;
                    if (ryRows)                  lines.push(`${ryRows} progress/journal row${ryRows === 1 ? '' : 's'}`);
                    if (r.booking_payments)      lines.push(`${r.booking_payments} payment${r.booking_payments === 1 ? '' : 's'}`);
                    if (r.booking_client_offers) lines.push(`${r.booking_client_offers} offer${r.booking_client_offers === 1 ? '' : 's'}`);
                    if (r.has_lead_row)          lines.push('1 lead row');
                    const isChecked = selectedUserIds.has(u.id);
                    return (
                      <div key={u.id} style={{
                        background: C.card,
                        border: `1px solid ${isChecked ? C.amber : C.border}`,
                        borderRadius: 8,
                        padding: '10px 14px',
                        display: 'flex',
                        gap: 12,
                        alignItems: 'flex-start',
                        opacity: isChecked ? 1 : 0.5,
                        transition: 'opacity 0.15s, border-color 0.15s',
                      }}>
                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={e => tdcToggleUser(u.id, e.target.checked)}
                          style={{
                            marginTop: 2,
                            width: 16, height: 16,
                            flexShrink: 0,
                            cursor: 'pointer',
                            accentColor: C.amber,
                          }}
                        />
                        {/* Row content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: C.heading }}>
                                {u.first_name} {u.last_name}
                              </div>
                              <code style={{ fontSize: 11, color: C.accent }}>{u.email}</code>
                            </div>
                            <div style={{ fontSize: 11, color: C.accent }}>
                              Created {new Date(u.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </div>
                          </div>
                          <div style={{ fontSize: 11, color: C.accent, marginTop: 6 }}>
                            {lines.length > 0 ? `${lines.join(', ')} will also be deleted` : 'No related rows.'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    onClick={tdcReset}
                    style={{
                      background: 'transparent', color: C.accent,
                      border: `1px solid ${C.border}`, borderRadius: 8,
                      padding: '10px 18px', fontSize: 13, fontWeight: 500,
                      cursor: 'pointer', fontFamily: FONT,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={tdcOpenConfirm}
                    disabled={selectedUserIds.size === 0}
                    style={{
                      background: selectedUserIds.size === 0 ? C.border : C.red,
                      color: selectedUserIds.size === 0 ? C.accent : '#fff',
                      border: 'none', borderRadius: 8, padding: '10px 18px',
                      fontSize: 13, fontWeight: 700,
                      cursor: selectedUserIds.size === 0 ? 'not-allowed' : 'pointer',
                      fontFamily: FONT,
                      transition: 'background 0.15s',
                    }}
                  >
                    {selectedUserIds.size === 0
                      ? 'Select at least one user'
                      : `Delete ${selectedUserIds.size} user${selectedUserIds.size === 1 ? '' : 's'}`}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Success state */}
        {tdcStage === 'success' && tdcResult && (
          <div style={{
            marginTop: 4,
            background: C.greenBg,
            border: '1px solid #A8D5C0',
            borderRadius: 10,
            padding: '16px 18px',
          }}>
            <p style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: C.green }}>
              ✓ Deleted {tdcResult.deletedSummary.ry_users} test user{tdcResult.deletedSummary.ry_users === 1 ? '' : 's'}
            </p>
            <ul style={{ margin: '0 0 14px', paddingLeft: 18, fontSize: 12, color: C.text, lineHeight: 1.7 }}>
              {Object.entries(tdcResult.deletedSummary).map(([k, v]) =>
                v > 0 ? (<li key={k}><strong>{v}</strong> {k.replace(/_/g, ' ')}</li>) : null
              )}
            </ul>
            <button
              onClick={tdcReset}
              style={{
                background: C.heading, color: '#fff', border: 'none',
                borderRadius: 8, padding: '8px 16px', fontSize: 12,
                fontWeight: 600, cursor: 'pointer', fontFamily: FONT,
              }}
            >
              Done
            </button>
          </div>
        )}
      </div>
      )}

      {/* Confirmation modal — full-screen overlay (owner-only) */}
      {user?.role === 'owner' && (tdcStage === 'confirm' || tdcStage === 'deleting') && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && tdcStage === 'confirm') tdcReset();
          }}
        >
          <div style={{
            background: C.card, borderRadius: 14, padding: '24px 26px',
            maxWidth: 460, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            border: `1px solid ${C.border}`,
          }}>
            <h3 style={{
              margin: '0 0 8px', fontFamily: "'Forum', serif",
              fontSize: '1.15rem', color: C.red, letterSpacing: '0.04em',
            }}>
              Permanently delete {selectedUserIds.size} test user{selectedUserIds.size === 1 ? '' : 's'}?
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: C.text, lineHeight: 1.6 }}>
              This will delete the listed users and all their related data. This cannot be undone.
            </p>
            <input
              type="text"
              value={tdcConfirmInput}
              onChange={(e) => setTdcConfirmInput(e.target.value)}
              placeholder="Type DELETE TEST USERS to confirm"
              disabled={tdcStage === 'deleting'}
              style={{
                width: '100%', padding: '10px 12px', fontSize: 13,
                border: `1px solid ${C.border}`, borderRadius: 8,
                fontFamily: FONT, marginBottom: 14, boxSizing: 'border-box',
                color: C.text, background: C.amberBg,
              }}
            />
            {tdcError && (
              <div style={{
                background: C.redBg, border: '1px solid #F5C6C0', borderRadius: 8,
                padding: '10px 12px', color: C.red, fontSize: 12, marginBottom: 12,
              }}>
                ❌ {tdcError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={tdcReset}
                disabled={tdcStage === 'deleting'}
                style={{
                  background: 'transparent', color: C.accent,
                  border: `1px solid ${C.border}`, borderRadius: 8,
                  padding: '9px 16px', fontSize: 13, fontWeight: 500,
                  cursor: tdcStage === 'deleting' ? 'not-allowed' : 'pointer',
                  fontFamily: FONT,
                }}
              >
                Cancel
              </button>
              <button
                onClick={tdcConfirmDelete}
                disabled={tdcStage === 'deleting' || tdcConfirmInput !== 'DELETE TEST USERS'}
                style={{
                  background: (tdcStage === 'deleting' || tdcConfirmInput !== 'DELETE TEST USERS') ? C.barTrack : C.red,
                  color: (tdcStage === 'deleting' || tdcConfirmInput !== 'DELETE TEST USERS') ? C.accent : '#fff',
                  border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13,
                  fontWeight: 700,
                  cursor: (tdcStage === 'deleting' || tdcConfirmInput !== 'DELETE TEST USERS') ? 'not-allowed' : 'pointer',
                  fontFamily: FONT,
                }}
              >
                {tdcStage === 'deleting' ? 'Deleting…' : 'Confirm delete'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
